import type { TokenResponse } from '@1webit/cvs-api-scheme';
import { QueryClient } from '@tanstack/react-query';
import type { JwtPayload } from 'jsonwebtoken';
import { jwtDecode } from 'jwt-decode';
import type { KyResponse, Options } from 'ky';
import ky from 'ky';
import { redirect } from 'next/navigation';

import { Cookies } from '@/types';
import { getCookie, getForwardedFor, getFullUrl, getResponse, isValueEmpty, setCookie } from '@/utils';
import { getAuthCookieOptions } from '@/utils/middleware';

interface ApiRequestOptions {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: any;
  headers?: HeadersInit;
  data?: unknown | FormData;
  responseType?: string;
}

interface ErrorResponse {
  code: number;
  message: string;
}

const Errors = {
  Unauthorized: 'Unauthorized: Failed to fetch token due to invalid credentials',
  Internal: 'Internal Server Error',
} as const;

// Endpointy volané bez přihlášení - na 401 se nesmí přesměrovat na login, chyba se řeší v UI.
const PUBLIC_ENDPOINTS = ['auth/reset', 'api/v1/hash'];

const isPublicEndpoint = (url: string) => PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint));

export class Client {
  client;
  private static instance: Client;

  constructor(
    prefixUrl = typeof window === 'undefined' ? process.env.NEXT_PUBLIC_NEXT_API_URL : '/api/proxy',
    throwHttpErrors = false,
  ) {
    this.client = ky.create({
      prefixUrl,
      throwHttpErrors,
      timeout: false,
      hooks: {
        beforeRequest: [
          async (request) => {
            // Browser never sees the token (it's httpOnly) - the same-origin proxy route attaches it server-side.
            if (typeof window !== 'undefined') {
              return;
            }

            try {
              const { headers } = await import('next/headers');
              const forwardedFor = getForwardedFor(await headers());

              if (forwardedFor) {
                request.headers.set('X-Forwarded-For', forwardedFor);
              }
            } catch {}

            let accessToken = await getCookie(Cookies.AccessToken);

            if (accessToken && !request.url.includes('auth/refresh')) {
              // Check if token is expired
              try {
                const decoded = jwtDecode<JwtPayload>(accessToken);

                const expiryDate = new Date(decoded.exp! * 1000);

                // If expiryDate is in past then refresh tokens
                if (expiryDate < new Date()) {
                  const tokens = await this.newTokens();

                  if (tokens && tokens.accessToken && tokens.refreshToken) {
                    accessToken = tokens.accessToken;

                    await setCookie(Cookies.AccessToken, accessToken, getAuthCookieOptions(accessToken));
                    await setCookie(Cookies.RefreshToken, tokens.refreshToken, getAuthCookieOptions(tokens.refreshToken));
                  }
                }
              } catch {
                // If token is invalid, proceed without it
                accessToken = undefined;
              }
            }

            if (accessToken) {
              request.headers.set('Authorization', `Bearer ${accessToken}`);
            }
          },
        ],
        afterResponse: [ // Or retry with a fresh token on a 403 error
          async (request, _options, response) => {
            if (response.status === 500 && !(await response.json<ErrorResponse>()).message) {
              throw new Error(Errors.Internal);
            }

            if (response.status === 401) {
              if (isPublicEndpoint(request.url)) {
                return;
              }

              // The proxy route already tried refreshing before returning 401 - browser has no way
              // to read the httpOnly refresh token itself, so there's nothing left to retry here.
              if (typeof window !== 'undefined') {
                return this.loginRedirect();
              }

              // Get a fresh token
              try {
                const tokens = await this.newTokens();

                if (!tokens || !tokens.accessToken || !tokens.refreshToken) {
                  return this.loginRedirect();
                }

                await setCookie(Cookies.AccessToken, tokens.accessToken, getAuthCookieOptions(tokens.accessToken));
                await setCookie(Cookies.RefreshToken, tokens.refreshToken, getAuthCookieOptions(tokens.refreshToken));

                // Retry with the token
                request.headers.set('Authorization', `Bearer ${tokens.accessToken}`);

                return ky(request);
              } catch {
                return this.loginRedirect();
              }
            }
          },
        ],
      },
    });
  }

  private loginRedirect = async (): Promise<never> => {
    const queryClient = new QueryClient();
    queryClient.cancelQueries();

    if (typeof window === 'undefined') {
      // AccessToken/RefreshToken are httpOnly - only server-side code can clear them.
      const { cookies, headers } = await import('next/headers');
      const cookieStore = await cookies();

      cookieStore.delete(Cookies.AccessToken);
      cookieStore.delete(Cookies.RefreshToken);
      cookieStore.delete(Cookies.LoggedUser);

      const fullUrl = getFullUrl(await headers());
      return redirect(`/auth/login${!isValueEmpty(fullUrl) && fullUrl !== '/' ? `?callbackUrl=${encodeURIComponent(fullUrl)}` : ''}`);
    }

    // Browser JS can't clear httpOnly cookies itself - the logout route does it server-side.
    await fetch('/api/logout', { method: 'POST' });
    const callbackUrl = `${window.location.pathname}${window.location.search}`;
    return window.location.href = `/auth/login${callbackUrl !== '/' ? `?callbackUrl=${encodeURIComponent(callbackUrl)}` : ''}` as never;
  };

  private async newTokens(): Promise<TokenResponse> {
    const originalRefreshToken = await getCookie(Cookies.RefreshToken);

    if (!originalRefreshToken) {
      return this.loginRedirect();
    }

    try {
      const decoded = jwtDecode<JwtPayload>(originalRefreshToken);

      const expiryDate = new Date(decoded.exp! * 1000);

      if (expiryDate < new Date()) {
        return this.loginRedirect();
      }
    } catch {
      // Invalid refresh token format
      return this.loginRedirect();
    }

    const response = await getResponse('postApiV1AuthRefresh', { token: originalRefreshToken });

    if (this.isErrorResponse(response)) {
      throw new Error(response.message);
    }

    if (!response.accessToken || !response.refreshToken) {
      throw new Error('Error response from auth refresh');
    }

    return response;
  }

  public static getInstance(): Client {
    if (!Client.instance) {
      Client.instance = new Client();
    }
    return Client.instance;
  }

  private async _handleResponse<T>(
    res: KyResponse,
  ): Promise<T | ErrorResponse> {
    const json = await res.json();

    if (this.isErrorResponse(json)) {
      return json as ErrorResponse;
    }

    return json as T;
  }

  private isErrorResponse(data: unknown): data is ErrorResponse {
    if (typeof data !== 'object' || data === null || Array.isArray(data)) {
      return false;
    }

    return (
      'status' in data
      && typeof data.status === 'string'
      && data.status === 'error'
      && 'code' in data
      && typeof data.code === 'number'
      && (data.code.toString().startsWith('4')
        || data.code.toString().startsWith('5'))
      && 'message' in data
      && typeof data.message === 'string'
    );
  }

  public async request<T>({
    url,
    method,
    headers = {},
    params,
    data,
  }: ApiRequestOptions): Promise<T | ErrorResponse> {
    if ((headers as Record<string, string>)['Content-Type'] === 'multipart/form-data') {
      delete (headers as Record<string, string>)['Content-Type'];
    }

    const methodMap: Record<
      string,
      (url: string, options?: Options) => Promise<KyResponse>
    > = {
      POST: this.client.post,
      PUT: this.client.put,
      DELETE: this.client.delete,
      GET: this.client.get,
    };

    if (!methodMap[method]) {
      throw new Error(`Unsupported method: ${method}`);
    }

    const options: Options = {
      headers,
      searchParams: params,
    };

    if (method !== 'GET') {
      if (data instanceof FormData) {
        options.body = data as FormData;
      } else {
        options.json = data;
      }
    }

    const response = await methodMap[method](url, options);

    const contentType = response.headers.get('Content-Type');
    if (contentType && !contentType.includes('application/json')) {
      return response as unknown as T;
    }

    return this._handleResponse<T>(response);
  }
}
